/**
 * DatePresetPills — one-click accounting date ranges.
 *
 * Renders 3-4 pill buttons ("This month", "Last month", "YTD", optional
 * "Last 30d") that push { from, to } as ISO date strings (YYYY-MM-DD) into
 * the supplied setters. Also highlights the pill whose range matches the
 * currently-selected dates so admins always see which slice they're viewing.
 *
 * Used by /admin/loyalty and /admin/bookkeeping so the two audit pages share
 * the exact same accounting semantics.
 */
export function DatePresetPills({ from, to, onChange, includeLast30 = false, className = "" }) {
  const today = new Date();
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();
  // Helper: format Date → YYYY-MM-DD in UTC (matches the date input format).
  const fmt = (d) => d.toISOString().slice(0, 10);

  const monthStart = fmt(new Date(Date.UTC(y, m, 1)));
  const monthEnd = fmt(new Date(Date.UTC(y, m + 1, 0)));
  const lastMonthStart = fmt(new Date(Date.UTC(y, m - 1, 1)));
  const lastMonthEnd = fmt(new Date(Date.UTC(y, m, 0)));
  const yearStart = fmt(new Date(Date.UTC(y, 0, 1)));
  const yearEnd = fmt(new Date(Date.UTC(y, 11, 31)));
  const last30 = new Date(); last30.setUTCDate(last30.getUTCDate() - 30);
  const last30Start = fmt(last30);
  const todayIso = fmt(today);

  const presets = [
    { id: "mtd", label: "This month", from: monthStart, to: monthEnd },
    { id: "last_month", label: "Last month", from: lastMonthStart, to: lastMonthEnd },
    { id: "ytd", label: "YTD", from: yearStart, to: yearEnd },
  ];
  if (includeLast30) presets.push({ id: "30d", label: "Last 30 days", from: last30Start, to: todayIso });

  const active = presets.find((p) => p.from === from && p.to === to)?.id;

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid="date-preset-pills">
      {presets.map((p) => {
        const isActive = active === p.id;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onChange(p.from, p.to)}
            className={`inline-flex items-center h-8 px-3 rounded-full text-[12px] font-medium border transition ${
              isActive
                ? "border-[var(--terra)] bg-[var(--terra)]/15 text-[var(--terra)]"
                : "border-[var(--line)] text-[var(--ink-soft)] hover:border-[var(--terra)] hover:text-[var(--terra)]"
            }`}
            data-testid={`date-preset-${p.id}`}
            aria-pressed={isActive || false}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}
